/**
 * PollModal — modal to participate in a poll on a shared partial game.
 *
 * Shows the game context (position, contract, tricks played),
 * the player's remaining hand, and lets them vote on which card to play.
 * Results are shown to the author and players who have voted.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { LibraryGameData, LibrarySharedGameSummary } from '../../types/library';
import type { CardNumber } from '../../types/game';
import { getCardShortName, sortHand } from '../../utils/cards';
import PollPanel from './PollPanel';
import { BACKEND_URL } from '../../config';

interface PollModalProps {
  pseudo: string;
  sharedGame: LibrarySharedGameSummary;
  onClose: () => void;
}

export default function PollModal({ pseudo, sharedGame, onClose }: PollModalProps) {
  const [gameData, setGameData] = useState<LibraryGameData | null>(null);
  const [recommendedPosition, setRecommendedPosition] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/library/shared/${sharedGame.id}?pseudo=${encodeURIComponent(pseudo)}`,
        );
        if (res.ok) {
          const data = await res.json();
          setGameData(data.game_data);
          setRecommendedPosition(data.recommended_position ?? 0);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [pseudo, sharedGame.id]);

  // Compute the remaining hand at the recommended position
  const { hand, trickCards } = useMemo(() => {
    if (!gameData) return { hand: [] as CardNumber[], trickCards: [] as CardNumber[] };

    const pos = recommendedPosition;
    const initialHand = gameData.initialHands[pos] ?? [];

    // Start with initial hand
    let remaining = [...initialHand];

    // If this player is the taker, apply ecart (receive chien, remove ecart)
    if (pos === gameData.taker && gameData.chien.length > 0) {
      remaining = [...remaining, ...gameData.chien];
      for (const c of gameData.ecart) {
        const idx = remaining.indexOf(c);
        if (idx >= 0) remaining.splice(idx, 1);
      }
    }

    // Remove cards played in completed tricks
    const tricks = gameData.tricks ?? [];
    for (const trick of tricks) {
      const myPlay = trick.cards.find(cp => cp.player === pos);
      if (myPlay) {
        const idx = remaining.indexOf(myPlay.card);
        if (idx >= 0) remaining.splice(idx, 1);
      }
    }

    // Current trick cards (the last trick if it's incomplete)
    let currentTrickCards: CardNumber[] = [];
    if (tricks.length > 0) {
      const lastTrick = tricks[tricks.length - 1];
      if (lastTrick.cards.length < gameData.playerCount) {
        currentTrickCards = lastTrick.cards.map(cp => cp.card);
      }
    }

    return {
      hand: sortHand(remaining),
      trickCards: currentTrickCards,
    };
  }, [gameData, recommendedPosition]);

  const cardName = useCallback((card: CardNumber) => getCardShortName(card), []);

  return (
    <div className="library-modal-overlay" onClick={onClose}>
      <div
        className="library-modal"
        style={{ maxWidth: '500px' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="library-modal-header">
          <h3>Sondage</h3>
          <button className="library-modal-close" onClick={onClose}>✕</button>
        </div>

        {loading && (
          <div className="library-comments-loading">Chargement…</div>
        )}

        {!loading && !gameData && (
          <div className="library-comments-empty">Impossible de charger le sondage.</div>
        )}

        {!loading && gameData && (
          <div className="library-modal-body" style={{ padding: '12px' }}>
            {/* Game context */}
            <div className="library-poll-context">
              <div className="library-poll-label">Contexte</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                {gameData.playerCount} joueurs
                {' · '}Position {recommendedPosition + 1}
                {gameData.tricks.length > 0 && (
                  <> · Pli {gameData.tricks.length}</>
                )}
              </div>

              {/* Show remaining hand */}
              {hand.length > 0 && (
                <>
                  <div className="library-poll-label">Votre main</div>
                  <div className="library-poll-cards" style={{ marginBottom: '10px' }}>
                    {hand.map(card => (
                      <span key={card} className="library-poll-card" style={{ cursor: 'default' }}>
                        {cardName(card)}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {/* Show current trick if any */}
              {trickCards.length > 0 && (
                <>
                  <div className="library-poll-label">Pli en cours</div>
                  <div className="library-poll-cards" style={{ marginBottom: '10px' }}>
                    {trickCards.map((card, i) => (
                      <span key={i} className="library-poll-card" style={{ cursor: 'default' }}>
                        {cardName(card)}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Poll voting / results */}
            <PollPanel
              pseudo={pseudo}
              sharedGameId={sharedGame.id}
              isAuthor={sharedGame.shared_by.toLowerCase() === pseudo.toLowerCase()}
              partialPhase={sharedGame.partial_phase}
            />
          </div>
        )}
      </div>
    </div>
  );
}
